import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { MFALoginSend } from "pwapi";

import { resetErrors } from "shared/actions/authActions";
import {
  new_login,
  mfaSelectMethod,
  barcodeLogin,
  getLocationUrl,
} from "shared/actions/authActions";
import { mfaLoginFormProps, useMfaLoginForm } from "shared/hooks/mfaHooks";

import useBarcodeScanner from "hooks/useBarcodeScanner";
import LoginLayout from "ui/components/LoginLayout";
import MFACodeInput from "ui/components/MFACodeInput";
import PasswordEyeButton from "ui/components/PasswordEyeButton";

import { useAppSelector } from "../app/hooks";

export const extractErrorMessages = (errors: any, key: string) => {
  if (errors && errors[key] && errors[key].length > 0) {
    return (
      <div>
        {errors[key].map((e: string) => (
          <span>{e}</span>
        ))}
      </div>
    );
  }
  return "";
};

interface LoginErrorProps {
  authErrors: any;
}
const LoginError = ({ authErrors }: LoginErrorProps) => {
  if (!authErrors?.non_field_errors && !authErrors?.detail) return null;
  return (
    <div className="message_container">
      <div className="alert alert-danger">
        {authErrors.non_field_errors || ""}
        {authErrors.detail || ""}
      </div>
    </div>
  );
};

const LoginForm = ({
  errors,
  touched,
  handleSubmit,
  handleReset,
  values,
  setFieldValue,
  loginWithSSO,
}: mfaLoginFormProps & { loginWithSSO: Function }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    authErrors,
    mfaCodeRequired,
    mfaMethod,
    sendMfaCode,
    reset,
    canResend,
    mfaMethodSentByText,
  } = useMfaLoginForm({
    touched,
    values,
    handleReset,
    handleSubmit,
    setFieldValue,
  });

  const [showPassword, setShowPassword] = useState(false);

  const { REACT_APP_USE_SSO_LOGIN } = process.env;

  return (
    <Form onSubmit={handleSubmit} translate="yes">
      <LoginError authErrors={authErrors} />
      <Field
        type="text"
        name="username"
        id="username"
        placeholder="Username"
        maxLength={150}
        disabled={mfaCodeRequired}
        required
      />
      {errors && errors.username && touched.username && authErrors.username}

      {!mfaCodeRequired ? (
        <div className="LoginForm__password">
          <Field
            type={showPassword ? "text" : "password"}
            name="password"
            id="password"
            placeholder="Password"
            required
          />
          <PasswordEyeButton
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
          {errors && errors.password && touched.password && authErrors.password}
        </div>
      ) : !mfaMethod ? (
        <>
          <em className="mfa-prefix">Pick an authentication method:</em>
          <div className="mfa-method-buttons">
            <button
              className="Button Button--purple"
              type="button"
              onClick={() =>
                dispatch(mfaSelectMethod(MFALoginSend.mfa_method.EMAIL))
              }
            >
              Email
            </button>
            <button
              className="Button Button--purple"
              type="button"
              onClick={() =>
                dispatch(mfaSelectMethod(MFALoginSend.mfa_method.SMS))
              }
            >
              SMS
            </button>
          </div>
        </>
      ) : (
        <>
          <em className="mfa-prefix">
            Enter the authentication code {mfaMethodSentByText[mfaMethod]}
          </em>
          <Field name="mfa_code">
            {({ field }: any) => (
              <MFACodeInput
                value={field.value || ""}
                onChange={(value: string) => setFieldValue("mfa_code", value)}
              />
            )}
          </Field>
          {errors && errors.mfa_code && touched.mfa_code && authErrors.mfa_code}
        </>
      )}

      {!!mfaMethod || !mfaCodeRequired ? (
        <div className="mfa-form-buttons">
          {mfaCodeRequired ? (
            <button className="Button--text" type="button" onClick={reset}>
              CANCEL
            </button>
          ) : (
            <>
              <button
                className="Button Button--purple login__button"
                type="submit"
                disabled={!(values.password && values.username)}
              >
                LOG&nbsp;IN
              </button>
              {REACT_APP_USE_SSO_LOGIN === "true" && (
                <button
                  className="Button Button--lightGrey login__button login__button--sso"
                  type="button"
                  onClick={() => loginWithSSO()}
                >
                  LOG&nbsp;IN&nbsp;WITH&nbsp;SSO
                </button>
              )}
              <button
                className="Button--text"
                style={{ width: "auto", whiteSpace: "nowrap" }}
                type="button"
                onClick={() => navigate("/password-reset")}
              >
                Forgot Password?
              </button>
            </>
          )}
          {["email", "sms"].includes(mfaMethod) && (
            <button
              className="Button--text"
              type="button"
              onClick={sendMfaCode}
              disabled={!canResend}
            >
              RESEND
            </button>
          )}
        </div>
      ) : (
        <div className="mfa-buttons">
          <button className="Button--text" type="button" onClick={reset}>
            CANCEL
          </button>
        </div>
      )}

      {!!mfaMethod && (
        <div className="mfa-buttons">
          <button
            className="Button--text"
            type="button"
            onClick={() => dispatch(mfaSelectMethod(undefined))}
          >
            Change Authentication Method
          </button>
        </div>
      )}
    </Form>
  );
};

const LoginPage = () => {
  const authErrors = useAppSelector(({ auth }) => auth.errors || {});

  const hasErrors = Object.keys(authErrors).length > 0;

  const dispatch = useDispatch();
  const [scanningLoginToken, setScanningLoginToken] = useState("");
  const [showPin, setShowPin] = useState(false);

  useBarcodeScanner((barcode) => {
    barcode.object_token && setScanningLoginToken(barcode.object_token);
  });

  const [pin, setPin] = useState("");

  const resetErrorState = () => {
    if (hasErrors) {
      dispatch(resetErrors());
    }
  };

  const handleLogin = () => {
    if (!scanningLoginToken || !pin || pin.length < 4) return;
    dispatch(barcodeLogin(scanningLoginToken, pin));
  };

  const handleLoginWithSSO = () => {
    dispatch(new_login({ is_sso: true, url: getLocationUrl(window.location) }));
  };

  useEffect(() => {
    if (pin.length === 4) {
      handleLogin();
    }
    if (hasErrors) {
    }
  }, [pin]);

  return (
    <LoginLayout>
      <div className="LoginPage">
        {scanningLoginToken ? (
          <main className="BarcodeLogin">
            <form className="BarcodeLogin__Form">
              <LoginError authErrors={authErrors} />
              <div className="BarcodeLogin__upper"></div>
              <div className="LoginForm__password">
                <input
                  className="BarcodeLogin__number"
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={(e) => {
                    setPin(e.target.value);
                  }}
                  maxLength={4}
                />
                {pin.length > 0 && (
                  <PasswordEyeButton
                    showPassword={showPin}
                    setShowPassword={setShowPin}
                  />
                )}
              </div>
              <div className="BarcodeLogin__keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setPin((p) => (p.length < 4 ? `${p}${i}` : p));
                      resetErrorState();
                    }}
                  >
                    {i}
                  </button>
                ))}
                <button
                  type="button"
                  className="BarcodeLogin__keypad__delete"
                  onClick={() => {
                    setPin((p) => p.slice(0, -1));
                    resetErrorState();
                  }}
                >
                  &larr;
                </button>
              </div>
              <button
                type="button"
                title="Back to Password Login"
                className="Button--text"
                style={{ padding: "1.5em", marginTop: "1em" }}
                onClick={() => setScanningLoginToken("")}
              >
                Back to Password Login
              </button>
            </form>
          </main>
        ) : (
          <Formik
            initialValues={{ username: "", password: "", mfa_code: undefined }}
            onSubmit={(values, { setSubmitting }) => {
              dispatch(
                new_login({ ...values, url: getLocationUrl(window.location) })
              );
              setSubmitting(false);
            }}
          >
            {(props) => (
              <LoginForm {...props} loginWithSSO={handleLoginWithSSO} />
            )}
          </Formik>
        )}
      </div>
    </LoginLayout>
  );
};

export default LoginPage;
